/**
 * Reconciling a planned order against what the scheme actually accepts.
 *
 * The planner sizes an instalment from the goal's arithmetic — ₹7,73,689.31 a
 * month, monthly, for 72 instalments. The order gateway will not take that. It
 * takes amounts on a fixed step, within a floor and a ceiling, at one of the
 * frequencies the scheme publishes, on one of the days it publishes, for at
 * least a minimum number of instalments — and only if the scheme is open to the
 * operation at all.
 *
 * None of that is in the plan. All of it is in `get_schemes_by_isin`, which the
 * planning engine never calls. So between "here is your plan" and "place it"
 * there is a reconciliation step, and this file is it.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 *
 * It does not quietly make the order fit. A rupee of rounding is noise and is
 * applied silently; a change of frequency is not — it moves the instalment by a
 * multiple and changes what the goal is funded to, against a report the client
 * is already holding. Those are surfaced and left for a person to decide,
 * because the alternative is placing something the document does not describe.
 *
 * The verdict grades exactly that difference:
 *
 *   ready         placeable as planned
 *   adjusted      placeable, with a difference small enough to state and apply
 *   needs-choice  placeable once someone picks something the plan never carried
 *   blocked       not placeable; the plan itself has to change
 */

import {
  getFundOperationAvailability,
  getInvestmentAmountRange,
  getSchemeLockIn,
  getSipInvestmentAmountRanges,
} from "./fundRules";
import type { InvestmentAmountRange } from "./fundRules";
import type { MFSchemeDetails } from "../../Api/mfAdminInvestments";
import { formatMFInstallmentDay, mfFrequencyNeedsInstallmentDay } from "../mfSchedule";
import type { PlannerOrder } from "./types";

export type OrderVerdict =
  | "checking"
  | "unavailable"
  | "ready"
  | "adjusted"
  | "needs-choice"
  | "blocked";

/** One line of the plan-versus-fund comparison. */
export interface ComparisonRow {
  label: string;
  /** What the plan asked for. "—" where the plan carries nothing. */
  planned: string;
  /** What the scheme permits. */
  allowed: string;
  /** What would actually be sent. */
  placing: string;
  state: "match" | "adjusted" | "choice" | "blocked" | "absent";
  note?: string;
}

export interface OrderBlocker {
  title: string;
  detail: string;
  /** Where the fix lives. Nothing on this screen can resolve a "plan" blocker. */
  owner: "plan" | "scheme" | "backend";
}

/** What a person still has to pick before the order can go. */
export type OrderChoice = "frequency" | "installmentDay";

export interface OrderSelection {
  /** API form — lowercase, e.g. "monthly". */
  frequency?: string;
  installmentDay?: number;
}

export interface OrderReconciliation {
  orderId: string;
  verdict: OrderVerdict;
  /** Amount that would be sent, after alignment. */
  amount: number;
  /** `amount` − the planned amount. Zero when nothing moved. */
  amountDelta: number;
  frequency: string | null;
  installments: number | null;
  installmentDay: number | null;
  /** Frequencies this scheme publishes an SIP threshold for. */
  availableFrequencies: string[];
  availableDates: number[];
  lockIn: boolean;
  /** Months. The API returns months even though the AMC quotes years. */
  lockInPeriod: number | null;
  rows: ComparisonRow[];
  blockers: OrderBlocker[];
  choicesNeeded: OrderChoice[];
  /** Cleared for the placement flow. */
  placeable: boolean;
}

/* ─────────────────────────────── helpers ────────────────────────────── */

/** Full figure to the paise, matching every other amount in the feature. */
const money = (value: number): string =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const titleFrequency = (frequency?: string | null): string => {
  if (!frequency) return "—";
  const bare = frequency.trim().toLowerCase();
  return bare ? bare[0].toUpperCase() + bare.slice(1) : "—";
};

/**
 * Whether a frequency carries no installment day at all — not a string compare
 * against "daily", which missed every other dayless spelling the API uses.
 */
const isDaily = (frequency?: string | null): boolean =>
  !mfFrequencyNeedsInstallmentDay(frequency || "");

/**
 * Nearest step, then clamped into the scheme's band.
 *
 * Nearest rather than up: the plan's figure is the honest one, and rounding it
 * away from itself by up to a full step in one direction biases every order the
 * same way. Only the floor and ceiling move it deliberately.
 */
const alignAmount = (
  amount: number,
  range: InvestmentAmountRange
): number => {
  const step = range.amountStep > 0 ? range.amountStep : 1;
  let aligned = Math.round(amount / step) * step;
  if (range.minAmount != null && aligned < range.minAmount) {
    aligned = range.minAmount;
  }
  if (range.maxAmount != null && aligned > range.maxAmount) {
    aligned = Math.floor(range.maxAmount / step) * step;
  }
  return Math.round(aligned * 100) / 100;
};

/** Months from today to the goal this order funds, or null when unknown. */
export interface OrderContext {
  /** Tenure of the goal or loan this order belongs to, in months. */
  monthsToGoal?: number | null;
  goalName?: string;
}

/* ────────────────────────────── the verdict ─────────────────────────── */

export const reconcileOrder = (
  order: PlannerOrder,
  fundDetails: MFSchemeDetails | undefined,
  context: OrderContext = {},
  selection: OrderSelection = {}
): OrderReconciliation => {
  const base: OrderReconciliation = {
    orderId: order.id,
    verdict: "checking",
    amount: order.amount,
    amountDelta: 0,
    frequency: order.sip?.frequency.toLowerCase() ?? null,
    installments: order.sip?.installments ?? null,
    installmentDay: selection.installmentDay ?? null,
    availableFrequencies: [],
    availableDates: [],
    lockIn: false,
    lockInPeriod: null,
    rows: [],
    blockers: [],
    choicesNeeded: [],
    placeable: false,
  };

  /*
   * A bond never reaches these endpoints. It goes through
   * order_management_v2/orders/, which needs the issuer image and the buyer's
   * UCC name and opens the dealsheet and RFQ journey — none of which exists on
   * this screen. Saying so is more useful than showing a disabled row.
   */
  if (order.kind === "bondLumpsum") {
    return {
      ...base,
      verdict: "blocked",
      blockers: [
        {
          title: "Bond orders are not placed here",
          detail:
            "This line goes through order_management_v2/orders/ — the dealsheet and RFQ journey, which needs the issuer image and the investor's UCC name. Neither is available on this screen, so the bond has to be raised separately from the bond desk.",
          owner: "backend",
        },
      ],
      rows: [
        {
          label: "Instrument",
          planned: order.name,
          allowed: "Bond desk only",
          placing: "Not placed",
          state: "blocked",
        },
      ],
    };
  }

  if (!fundDetails) {
    return {
      ...base,
      verdict: "unavailable",
      blockers: [
        {
          title: "Scheme rules could not be loaded",
          detail:
            "The order gateway's limits for this scheme were not returned, so the amount, frequency and dates cannot be checked. Nothing is placed on the investor's behalf without them.",
          owner: "backend",
        },
      ],
    };
  }

  const isSip = order.kind === "mutualFundSip";
  const operation = isSip ? "sip" : "lumpsum";
  const availability = getFundOperationAvailability(fundDetails, operation);

  const sipRanges = getSipInvestmentAmountRanges(fundDetails);
  const availableFrequencies = sipRanges
    .map((range) => range.frequency)
    .filter((frequency): frequency is string => !!frequency);

  const plannedFrequency = order.sip?.frequency.toLowerCase() ?? null;
  const chosenFrequency = selection.frequency ?? null;
  const frequencyIsOffered =
    !!plannedFrequency && availableFrequencies.includes(plannedFrequency);
  const effectiveFrequency = isSip
    ? chosenFrequency ?? (frequencyIsOffered ? plannedFrequency : null)
    : null;

  const range = isSip
    ? getInvestmentAmountRange(fundDetails, "sip", effectiveFrequency ?? undefined)
    : getInvestmentAmountRange(fundDetails, "lumpsum");

  const rows: ComparisonRow[] = [];
  const blockers: OrderBlocker[] = [];
  const choicesNeeded: OrderChoice[] = [];

  /* ── is the scheme even open to this? ── */
  if (!availability.isAllowed) {
    blockers.push({
      title: availability.shortReason ?? "Not available",
      detail: availability.reason ?? "This scheme is not accepting this order.",
      owner: "scheme",
    });
  }

  /* ── frequency ── */
  if (isSip) {
    const state: ComparisonRow["state"] = !availableFrequencies.length
      ? "blocked"
      : frequencyIsOffered
        ? "match"
        : chosenFrequency
          ? "adjusted"
          : "choice";

    rows.push({
      label: "Frequency",
      planned: titleFrequency(plannedFrequency),
      allowed: availableFrequencies.length
        ? availableFrequencies.map(titleFrequency).join(", ")
        : "No SIP frequency published",
      placing: titleFrequency(effectiveFrequency),
      state,
      note:
        state === "choice"
          ? `This scheme does not offer a ${titleFrequency(plannedFrequency).toLowerCase()} SIP. Choosing another changes the instalment and the number of instalments, which changes what this goal is funded to.`
          : state === "adjusted"
            ? "Changed from the plan. The goal's funding no longer matches the report."
            : undefined,
    });

    if (!availableFrequencies.length) {
      blockers.push({
        title: "This scheme publishes no SIP frequency",
        detail:
          "The order gateway returned no SIP threshold for this scheme, so there is no frequency, amount band or instalment date to place against.",
        owner: "scheme",
      });
    } else if (!frequencyIsOffered && !chosenFrequency) {
      choicesNeeded.push("frequency");
    }
  }

  /* ── amount ── */
  const alignedAmount = range.isAvailable
    ? alignAmount(order.amount, range)
    : order.amount;
  const amountDelta = Math.round((alignedAmount - order.amount) * 100) / 100;
  const amountMoved = Math.abs(amountDelta) >= 0.01;

  rows.push({
    label: isSip ? "Instalment" : "Amount",
    planned: money(order.amount),
    allowed: range.isAvailable
      ? `${money(range.minAmount ?? 0)} – ${money(range.maxAmount ?? 0)}, in steps of ${money(range.amountStep)}`
      : "Not published",
    placing: range.isAvailable ? money(alignedAmount) : "—",
    state: !range.isAvailable ? "blocked" : amountMoved ? "adjusted" : "match",
    note: !range.isAvailable
      ? undefined
      : amountMoved
        ? `${amountDelta > 0 ? "Rounded up" : "Rounded down"} by ${money(Math.abs(amountDelta))} to meet the scheme's step.`
        : undefined,
  });

  if (!range.isAvailable) {
    blockers.push({
      title: "No amount band published",
      detail:
        "The scheme returned no minimum, maximum or step for this operation, so there is nothing to validate the amount against.",
      owner: "scheme",
    });
  }

  /* ── instalments ── */
  if (isSip) {
    const planned = order.sip?.installments ?? 0;
    const minimum = range.installmentsMin;
    const short = minimum != null && planned < minimum;
    rows.push({
      label: "Instalments",
      planned: planned ? String(planned) : "—",
      allowed: minimum != null ? `At least ${minimum}` : "Not published",
      placing: short ? String(minimum) : String(planned),
      state: short ? "adjusted" : "match",
      note: short
        ? `The plan's ${planned} instalments fall short of the scheme's minimum. Running to ${minimum} extends the SIP past the goal date in the report.`
        : undefined,
    });

    if (short) {
      blockers.push({
        title: "The plan's instalment count is below the scheme minimum",
        detail: `This scheme requires at least ${minimum} instalments; the plan sizes ${planned}. Placing it would run the SIP past the goal date, so the plan needs regenerating rather than the order adjusting.`,
        owner: "plan",
      });
    }
  }

  /* ── instalment day ── */
  if (isSip) {
    const daily = isDaily(effectiveFrequency);
    const dates = range.dates ?? [];
    const chosenDay = selection.installmentDay ?? null;
    const dayValid = chosenDay != null && dates.includes(chosenDay);

    rows.push({
      label: "Debit day",
      planned: "—",
      allowed: daily
        ? "Every day"
        : dates.length
          ? `${dates.length} day${dates.length === 1 ? "" : "s"} available`
          : "Not published",
      placing: daily
        ? "Every day"
        : dayValid
          ? formatMFInstallmentDay(effectiveFrequency || "", chosenDay as number)
          : "Not chosen",
      state: daily ? "match" : dayValid ? "adjusted" : "choice",
      // Only while it is still open. Once a day is picked the note has served
      // its purpose and just adds noise to a row that now reads fine.
      note:
        daily || dayValid
          ? undefined
          : "The plan carries no debit date — it sizes instalments, it does not schedule them. One has to be picked before the mandate can be created.",
    });

    if (!daily && !dayValid) {
      if (dates.length) choicesNeeded.push("installmentDay");
      else {
        blockers.push({
          title: "No debit dates published",
          detail:
            "This scheme returned no permitted SIP dates, so a mandate cannot be scheduled against it.",
          owner: "scheme",
        });
      }
    }
  }

  /* ── lock-in against the goal it funds ── */
  const { lockIn, lockInPeriod } = getSchemeLockIn(fundDetails);
  const monthsToGoal = context.monthsToGoal ?? null;
  const locksPastGoal =
    lockIn && lockInPeriod != null && monthsToGoal != null && lockInPeriod > monthsToGoal;

  if (lockIn) {
    rows.push({
      label: "Lock-in",
      planned: monthsToGoal != null ? `Needed in ${monthsToGoal} months` : "—",
      allowed: lockInPeriod != null ? `${lockInPeriod} months` : "Applies",
      placing: locksPastGoal ? "Locked past the goal" : "Within the horizon",
      state: locksPastGoal ? "blocked" : "match",
      note: locksPastGoal
        ? "The money would still be locked on the date the goal needs it."
        : undefined,
    });
  }

  if (locksPastGoal) {
    blockers.push({
      title: "The lock-in outlasts the goal",
      detail: `This scheme locks money for ${lockInPeriod} months, and ${context.goalName ?? "the goal"} needs it in ${monthsToGoal}. The planning engine never sees lock-in — it does not read the scheme master — so this was not caught when the plan was built.`,
      owner: "plan",
    });
  }

  /* ── the verdict ── */
  const verdict: OrderVerdict = blockers.length
    ? "blocked"
    : choicesNeeded.length
      ? "needs-choice"
      : rows.some((row) => row.state === "adjusted")
        ? "adjusted"
        : "ready";

  return {
    orderId: order.id,
    verdict,
    amount: alignedAmount,
    amountDelta,
    frequency: effectiveFrequency,
    installments: isSip
      ? Math.max(order.sip?.installments ?? 0, range.installmentsMin ?? 0)
      : null,
    installmentDay: selection.installmentDay ?? null,
    availableFrequencies,
    availableDates: range.dates ?? [],
    lockIn,
    lockInPeriod,
    rows,
    blockers,
    choicesNeeded,
    placeable: verdict === "ready" || verdict === "adjusted",
  };
};
